import { Recipe } from '../types/recipe';

/**
 * The language a recipe is currently *displayed* in, which is not the same as
 * the language it was written in.
 *
 * Deciding what to translate from `originalLanguage` alone is what left English
 * recipes stuck in French: once translated, an English-source recipe still
 * reports `originalLanguage: 'en'`, so a switch back to English looked like a
 * no-op and was skipped.
 *
 * Lives here rather than in `mealPlanTranslation` because the grocery list
 * needs it too, and that module reaches Firebase through
 * `recipeTranslationService` — importing it from the grocery path would drag
 * the whole SDK into a pure aggregation function and its tests.
 */
export function displayLanguageOf(recipe: Recipe): 'en' | 'fr' | 'unknown' {
  const original = recipe.originalLanguage;
  if (!original || original === 'unknown') return 'unknown';
  if (!recipe.isTranslated) return original;
  return original === 'fr' ? 'en' : 'fr';
}

/** The minimum a meal has to expose for the translation trigger to reason about it. */
export interface TranslatableMeal {
  recipeId: string;
  recipe?: Recipe;
}

/**
 * A stable signature of "which planned meals are still showing in the wrong
 * language", used as the effect dependency that triggers translation.
 *
 * Why a key rather than the meal array: `useMealPlanTranslation` used to depend
 * on `[currentLanguage, dispatch]` only, so it fired on mount and on a language
 * switch and never again. Every path that writes meals *after* mount —
 * `useGenerateWeek` (the "Re-generate" / Generate My Week button), a dragged
 * card, a plan arriving from Firestore — therefore left French recipes sitting
 * in an English app until the next reload. Depending on the meal array directly
 * would re-fire on every unrelated meal edit; this key only moves when the set
 * of wrongly-displayed recipes moves.
 *
 * Empty string means nothing needs translating. The target language is part of
 * the key so flipping language always produces a new one.
 *
 * Filtering mirrors `translateMealPlanRecipes`: it keys on what a recipe
 * currently *reads* as, so an already-translated recipe drops out of the key as
 * soon as its update is applied — which is what makes the effect converge
 * instead of looping.
 */
export function pendingTranslationKey(
  meals: TranslatableMeal[],
  targetLanguage: 'en' | 'fr',
): string {
  const pending = meals
    .filter(meal => {
      if (!meal.recipe || meal.recipeId === 'free-text') return false;
      const shown = displayLanguageOf(meal.recipe);
      return shown !== 'unknown' && shown !== targetLanguage;
    })
    .map(meal => meal.recipeId)
    .sort();

  if (pending.length === 0) return '';
  // Deduplicated: the same recipe planned twice in a week is one translation.
  return `${targetLanguage}:${[...new Set(pending)].join('|')}`;
}

/**
 * Whether a list may show this recipe to a reader of `lang`.
 *
 * `languages` is the card's readiness field (api/_lib/translationReadiness):
 * the languages the recipe is fully readable in. A document that has not been
 * given the field yet is readable only in its own language — the safe default
 * is to hide, because showing a French cook an English card is the bug (#737).
 */
export function isReadableIn(
  entry: { originalLanguage?: string; languages?: string[] },
  lang: 'en' | 'fr',
): boolean {
  if (Array.isArray(entry.languages)) return entry.languages.includes(lang);
  return entry.originalLanguage === lang;
}

/**
 * Whether a discovery surface (library, generator, search) offers this entry
 * to a reader of `lang`.
 *
 * The gate is for the **shared** catalogue only. A cook's own saved copy
 * (`source` other than `'base'`) is never hidden: it is theirs, there is no
 * other place to find it, and a saved copy may legitimately carry no
 * `languages` field at all. Bundled entries have no `source` and are the
 * shared catalogue in another coat, so they are gated.
 */
export function isOfferedTo(
  entry: { source?: string; originalLanguage?: string; languages?: string[] },
  lang: 'en' | 'fr',
): boolean {
  if (entry.source && entry.source !== 'base') return true;
  return isReadableIn(entry, lang);
}
